import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {
  dashboardViewRegionsStateDefault,
  DashboardViewRegionsStateModel,
  dashboardViewRegionsUniqueStateId,
} from './dashboard-view-regions.model';
import {SelectDefaultViewRegion, SelectViewRegion} from './dashboard-view-regions.actions';
import {
  dashboardViewRegionsCreateParentMap,
  dashboardViewRegionsFormFlatListMap,
  dashboardViewRegionsGetChainedIds,
} from '../../utils/dashboard-view-regions.utils';
import {LoadDashboardStudyDetailsSuccess} from '../../../dashboard-new/states/dashboard/dashboard.actions';

@State<DashboardViewRegionsStateModel>({
  name: dashboardViewRegionsUniqueStateId,
  defaults: dashboardViewRegionsStateDefault,
})
@Injectable()
export class DashboardViewRegionsState {
  @Selector()
  static viewRegionList(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['viewRegionList'] {
    return state.viewRegionList;
  }

  @Selector()
  static selectedViewRegion(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['selectedViewRegion'] {
    return state.selectedViewRegion;
  }

  @Selector()
  static firstLevelViewRegion(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['firstLevelViewRegion'] {
    return state.firstLevelViewRegion;
  }

  @Selector()
  static secondLevelViewRegion(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['secondLevelViewRegion'] {
    return state.secondLevelViewRegion;
  }

  @Selector()
  static thirdLevelViewRegion(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['thirdLevelViewRegion'] {
    return state.thirdLevelViewRegion;
  }

  @Selector()
  static fourthLevelViewRegion(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['fourthLevelViewRegion'] {
    return state.fourthLevelViewRegion;
  }

  @Selector()
  static fifthLevelViewRegion(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['fourthLevelViewRegion'] {
    return state.fifthLevelViewRegion;
  }

  @Selector()
  static viewRegionsParentMap(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['viewRegionsParentMap'] {
    return state.viewRegionsParentMap;
  }

  @Selector()
  static viewRegionsFlatListMap(state: DashboardViewRegionsStateModel): DashboardViewRegionsStateModel['viewRegionsFlatListMap'] {
    return state.viewRegionsFlatListMap;
  }

  // Observe the dashboard study details load and store the view regions in the state.
  @Action(LoadDashboardStudyDetailsSuccess)
  loadDashboardStudyDetailsSuccess(
    ctx: StateContext<DashboardViewRegionsStateModel>,
    {dashboardStudyDetails}: LoadDashboardStudyDetailsSuccess
  ): void {
    // Extract the view regions from the dashboard study details.
    const viewRegions = dashboardStudyDetails.view_regions;

    ctx.patchState({
      viewRegionList: viewRegions,
      viewRegionsParentMap: dashboardViewRegionsCreateParentMap(viewRegions),
      viewRegionsFlatListMap: dashboardViewRegionsFormFlatListMap(viewRegions),
    });

    ctx.dispatch(new SelectDefaultViewRegion());
  }

  @Action(SelectViewRegion)
  selectViewRegion(ctx: StateContext<DashboardViewRegionsStateModel>, {viewRegionId}: SelectViewRegion): void {
    const state = ctx.getState();

    // Get the chain of parent view regions to the current selected region.
    const chainedIds = dashboardViewRegionsGetChainedIds(viewRegionId, state.viewRegionsParentMap);

    const firstLevelViewRegion = chainedIds[0] ? state.viewRegionsFlatListMap.get(chainedIds[0]) : null;
    const secondLevelViewRegion = chainedIds[1] ? state.viewRegionsFlatListMap.get(chainedIds[1]) : null;
    const thirdLevelViewRegion = chainedIds[2] ? state.viewRegionsFlatListMap.get(chainedIds[2]) : null;
    const fourthLevelViewRegion = chainedIds[3] ? state.viewRegionsFlatListMap.get(chainedIds[3]) : null;
    const fifthLevelViewRegion = chainedIds[4] ? state.viewRegionsFlatListMap.get(chainedIds[4]) : null;

    // Region with the highest level is selected as the main view region as it's the region where the chain starts.
    const selectedViewRegion = fifthLevelViewRegion || fourthLevelViewRegion || thirdLevelViewRegion || secondLevelViewRegion || firstLevelViewRegion;

    // Update the selected view regions in the state based on the chained ids.
    ctx.patchState({
      selectedViewRegion,
      firstLevelViewRegion,
      secondLevelViewRegion,
      thirdLevelViewRegion,
      fourthLevelViewRegion,
      fifthLevelViewRegion,
    });
  }

  @Action(SelectDefaultViewRegion)
  selectDefaultViewRegion(ctx: StateContext<DashboardViewRegionsStateModel>): void {
    // Extract the view region list from the state.
    const viewRegionList = ctx.getState()?.viewRegionList;

    // Ignore the action if the view region list is empty.
    if (!viewRegionList?.length) {
      return;
    }

    // Extract the first view region from the list.
    const viewRegion = viewRegionList[0];

    // Update the state with the extracted view region.
    ctx.patchState({
      selectedViewRegion: viewRegion,
      firstLevelViewRegion: viewRegion,
      secondLevelViewRegion: null,
      thirdLevelViewRegion: null,
      fourthLevelViewRegion: null,
      fifthLevelViewRegion: null,
    });
  }
}
